import { createApi } from "@reduxjs/toolkit/query/react";
import { publicBaseQuery } from "../base/public/publicBaseQuery";
import {
  ACCESS_TOKEN_KEY,
  WS_ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  AUTH_TOKEN_PASSWORD_GRANT,
  AUTH_TOKEN_WS_PASSWORD_GRANT,
  AUTH_REFRESH_TOKEN_PASSWORD_GRANT,
  AUTH_STRATEGY,
  TOKEN_PASSWORD_GRANT,
  TOKEN_WS_PASSWORD_GRANT,
} from "../base/private/axiosPrivateClient";
import { unwrapEnvelope, type LoginRequest, type LoginResponse } from "@/types/canonical";

type NCALayerLoginRequest = {
  signature: string;
};

type AzureLoginRequest = {
  code: string;
  redirect_uri: string;
  nonce?: string;
};

type SchoolRegisterRequest = {
  school: {
    name: string;
    short_description: string;
    full_description: string;
    website: string;
    phone: string;
    email: string;
  };
  user: {
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    password: string;
  };
};

type StudentRegisterRequest = {
  invite_code: string;
  firstname: string;
  lastname: string;
  patronymic?: string | null;
  phone?: string | null;
  email: string;
  password: string;
  recaptcha_token?: string | null;
  agree: boolean | number;
};

type StudentRegisterResponse = {
  access_token?: string;
  refresh_token?: string;
  status?: string;
  status_code?: number;
  errors?: Record<string, string[]>;
};

export const authPublicApi = createApi({
  reducerPath: "authPublicApi",
  baseQuery: publicBaseQuery(),
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/oauth/token",
        method: "POST",
        data: {
          grant_type: "password",
          client_id: process.env.NEXT_PUBLIC_PASSPORT_PASSWORD_GRANT_ID,
          client_secret: process.env.NEXT_PUBLIC_PASSPORT_PASSWORD_GRANT_SECRET,
          username: credentials.username,
          password: credentials.password,
        },
      }),
      transformResponse: (response: LoginResponse) => {
        const core = unwrapEnvelope(response);

        if (core.access_token) {
          localStorage.setItem(ACCESS_TOKEN_KEY, core.access_token);
          const bearerToken = `Bearer ${core.access_token}`;
          localStorage.setItem(AUTH_TOKEN_PASSWORD_GRANT, bearerToken);
          localStorage.setItem(AUTH_STRATEGY, "password_grant");
          localStorage.setItem(TOKEN_PASSWORD_GRANT, bearerToken);
          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event("auth-token-changed"));
          }
        }

        // WebSocket токен (если присутствует)
        if (core.ws_access_token) {
          localStorage.setItem(WS_ACCESS_TOKEN_KEY, core.ws_access_token);
          localStorage.setItem(AUTH_TOKEN_WS_PASSWORD_GRANT, core.ws_access_token);
          localStorage.setItem(TOKEN_WS_PASSWORD_GRANT, core.ws_access_token);
        } else {
          console.warn('[AUTH] ⚠ ws_access_token NOT found in response!');
        }

        if (core.refresh_token) {
          localStorage.setItem(REFRESH_TOKEN_KEY, core.refresh_token);
          localStorage.setItem(AUTH_REFRESH_TOKEN_PASSWORD_GRANT, core.refresh_token);
        } else {
          localStorage.setItem(AUTH_REFRESH_TOKEN_PASSWORD_GRANT, "false");
        }
        return response;
      },
    }),
    loginWithNCALayer: builder.mutation<LoginResponse, NCALayerLoginRequest>({
      query: (data) => ({
        url: "/oauth/token",
        method: "POST",
        data: {
          grant_type: "ncalayer",
          client_id: process.env.NEXT_PUBLIC_PASSPORT_NCALAYER_GRANT_ID,
          client_secret: process.env.NEXT_PUBLIC_PASSPORT_NCALAYER_GRANT_SECRET,
          scope: "*",
          signature: data.signature,
        },
      }),
      transformResponse: (response: LoginResponse) => {
        const core = unwrapEnvelope(response);

        if (core.access_token) {
          localStorage.setItem(ACCESS_TOKEN_KEY, core.access_token);
          const bearerToken = `Bearer ${core.access_token}`;
          localStorage.setItem(AUTH_TOKEN_PASSWORD_GRANT, bearerToken);
          localStorage.setItem(AUTH_STRATEGY, "ncalayer_grant");
          localStorage.setItem(TOKEN_PASSWORD_GRANT, bearerToken);
          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event("auth-token-changed"));
          }
        }

        if (core.ws_access_token) {
          localStorage.setItem(WS_ACCESS_TOKEN_KEY, core.ws_access_token);
          localStorage.setItem(AUTH_TOKEN_WS_PASSWORD_GRANT, core.ws_access_token);
          localStorage.setItem(TOKEN_WS_PASSWORD_GRANT, core.ws_access_token);
        }

        if (core.refresh_token) {
          localStorage.setItem(REFRESH_TOKEN_KEY, core.refresh_token);
          localStorage.setItem(AUTH_REFRESH_TOKEN_PASSWORD_GRANT, core.refresh_token);
        } else {
          localStorage.setItem(AUTH_REFRESH_TOKEN_PASSWORD_GRANT, "false");
        }

        return response;
      },
    }),
    loginWithAzure: builder.mutation<LoginResponse, AzureLoginRequest>({
      query: (data) => ({
        url: "/api/auth/azure/exchange",
        method: "POST",
        data: {
          code: data.code,
          redirect_uri: data.redirect_uri,
          nonce: data.nonce,
        },
      }),
      transformResponse: (response: LoginResponse) => {
        const core = unwrapEnvelope(response);

        if (core.access_token) {
          localStorage.setItem(ACCESS_TOKEN_KEY, core.access_token);
          const bearerToken = `Bearer ${core.access_token}`;
          localStorage.setItem(AUTH_TOKEN_PASSWORD_GRANT, bearerToken);
          localStorage.setItem(AUTH_STRATEGY, "password_grant");
          localStorage.setItem(TOKEN_PASSWORD_GRANT, bearerToken);
          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event("auth-token-changed"));
          }
        }

        if (core.ws_access_token) {
          localStorage.setItem(WS_ACCESS_TOKEN_KEY, core.ws_access_token);
          localStorage.setItem(AUTH_TOKEN_WS_PASSWORD_GRANT, core.ws_access_token);
          localStorage.setItem(TOKEN_WS_PASSWORD_GRANT, core.ws_access_token);
        }

        if (core.refresh_token) {
          localStorage.setItem(REFRESH_TOKEN_KEY, core.refresh_token);
          localStorage.setItem(AUTH_REFRESH_TOKEN_PASSWORD_GRANT, core.refresh_token);
        } else {
          localStorage.setItem(AUTH_REFRESH_TOKEN_PASSWORD_GRANT, "false");
        }
        return response;
      },
    }),
    register: builder.mutation<any, { email: string; password: string }>({
      query: (data) => ({
        url: "/api/register",
        method: "POST",
        data,
        baseURL: "",
      }),
    }),
    schoolRegister: builder.mutation<any, SchoolRegisterRequest>({
      query: (data) => ({
        url: "/api/school/register/submit.json",
        method: "POST",
        data,
      }),
    }),
    studentRegisterValidate: builder.mutation<StudentRegisterResponse, StudentRegisterRequest>({
      query: (data) => ({
        url: "/api/register/validate.json",
        method: "POST",
        data,
      }),
    }),
    studentRegister: builder.mutation<StudentRegisterResponse, StudentRegisterRequest>({
      query: (data) => ({
        url: "/api/register/submit.json",
        method: "POST",
        data,
      }),
      transformResponse: (response: StudentRegisterResponse) => {
        // Next route handler similar to `/api/auth/login`.
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("auth-token-changed"));
        }
        return response;
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginWithNCALayerMutation,
  useLoginWithAzureMutation,
  useRegisterMutation,
  useSchoolRegisterMutation,
  useStudentRegisterValidateMutation,
  useStudentRegisterMutation,
} = authPublicApi;
